import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/router';

// TODO: prototype (youtube privacy compliance). Site-wide switch between the
// two remediation options under evaluation:
//   - ?v=vimeo        -> every YouTube player renders a Vimeo player instead,
//                        no consent gate (Vimeo is embedded with dnt=1 and
//                        sets no cookies).
//   - no query param  -> YouTube players are gated behind a consent prompt
//                        ("accept cookies & play" or "watch on YouTube").
// If this ships, the mode becomes a build-time/site setting instead of a
// query param and this comment block goes away.

export type VideoPrivacyMode = 'consent' | 'vimeo';

interface VideoPrivacyContextType {
  mode: VideoPrivacyMode;
  // True when playing YouTube in-place is allowed (consent granted)
  consented: boolean;
  // Identifies the gate whose accept button granted consent. Null when consent
  // came from anywhere else (the CookieYes banner, a prior page load). Consent
  // is global, so every mounted gate clears at once; embeds compare this
  // against their own token to tell "the user asked for me" from "someone
  // else's accept swept me along"
  granterToken: string | null;
  // Grant consent: clicks the real CookieYes accept button when the banner
  // script is present, falls back to a local flag for environments without
  // CookieYes (local dev)
  accept: (token?: string) => void;
}

const VideoPrivacyContext = createContext<VideoPrivacyContextType | undefined>(undefined);

// Default-deny at module load: child components pre-warm the YouTube iframe
// API in their mount effects, which run BEFORE the provider's first effect
// (React runs effects bottom-up), so the flag must already be false by then.
// The provider effect below flips it to true once consent is confirmed.
if (typeof window !== 'undefined') {
  (window as any).__rhYtApiAllowed = false;
}

// TODO: prototype — CookieYes category that governs YouTube embeds. The
// production blocklist swallows the iframe_api script pre-consent; "functional"
// is the category CookieYes files embedded-video providers under.
const GATE_CATEGORY = 'functional';
// TODO: prototype-only fallback storage for environments without CookieYes
const FALLBACK_STORAGE_KEY = 'rh-video-consent';

// Categories the gate grants, and every non-necessary category configured on
// the account. Anything omitted from GRANT_CATEGORIES must be explicitly
// unchecked, otherwise a switch left on by the visitor would ride along.
const GRANT_CATEGORIES = ['functional', 'analytics'];
const OPTIONAL_CATEGORIES = ['functional', 'analytics', 'performance', 'advertisement', 'other'];

// CookieYes' accept_partial action does not take categories: it reads each
// one's scope off the preference-center switches, taking
// `#ckySwitch<slug> || #ckyCategoryDirect<slug>` checked. Both variants have to
// be written for the commit to be unambiguous. Returns false when no switch
// was found, so callers can avoid committing an all-reject by accident.
const setCategorySwitches = (granted: string[]): boolean => {
  let found = false;
  OPTIONAL_CATEGORIES.forEach((slug) => {
    const on = granted.includes(slug);
    ['ckySwitch', 'ckyCategoryDirect'].forEach((prefix) => {
      const el = document.getElementById(`${prefix}${slug}`) as HTMLInputElement | null;
      if (!el) return;
      el.checked = on;
      found = true;
    });
  });
  return found;
};

const readConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const cky = (window as any).getCkyConsent?.();
  if (cky) {
    return Boolean(cky.isUserActionCompleted && cky.categories?.[GATE_CATEGORY]);
  }
  try {
    return window.localStorage.getItem(FALLBACK_STORAGE_KEY) === 'granted';
  } catch {
    return false;
  }
};

export const VideoPrivacyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();
  const mode: VideoPrivacyMode = router?.query?.v === 'vimeo' ? 'vimeo' : 'consent';
  const [consented, setConsented] = useState(false);
  const [granterToken, setGranterToken] = useState<string | null>(null);

  // Consent state lives outside React (CookieYes cookie / localStorage), so
  // sync it on mount and whenever CookieYes broadcasts an update
  useEffect(() => {
    setConsented(readConsent());
    const onUpdate = () => setConsented(readConsent());
    document.addEventListener('cookieyes_consent_update', onUpdate);
    return () => document.removeEventListener('cookieyes_consent_update', onUpdate);
  }, []);

  // The YouTube iframe_api pre-warm in core/utils runs outside React, so it
  // reads this window flag: never allowed in vimeo mode, gated on consent
  // otherwise (undefined means "no provider mounted, behave as before")
  useEffect(() => {
    (window as any).__rhYtApiAllowed = mode === 'vimeo' ? false : consented;
    return () => { (window as any).__rhYtApiAllowed = false; };
  }, [mode, consented]);

  const accept = useCallback((token?: string) => {
    const performBannerAction = (window as any).performBannerAction;
    // Real CookieYes flow, scoped to GRANT_CATEGORIES rather than accept-all.
    // The cookieyes_consent_update listener will confirm the state, but set it
    // optimistically so the player swaps in without waiting.
    if (typeof performBannerAction === 'function' && setCategorySwitches(GRANT_CATEGORIES)) {
      performBannerAction('accept_partial');
    } else {
      try {
        window.localStorage.setItem(FALLBACK_STORAGE_KEY, 'granted');
      } catch { /* storage unavailable, session-only consent */ }
    }
    setGranterToken(token ?? null);
    setConsented(true);
  }, []);

  return (
    <VideoPrivacyContext.Provider value={{ mode, consented, granterToken, accept }}>
      {children}
    </VideoPrivacyContext.Provider>
  );
};

export const useVideoPrivacy = (): VideoPrivacyContextType => {
  const context = useContext(VideoPrivacyContext);
  // Safe defaults when the provider isn't mounted: consent granted so
  // existing player behavior is unchanged (mirrors useYoutubeVideo's fallback)
  return context || { mode: 'consent', consented: true, granterToken: null, accept: () => {} };
};
